/* System defined core library */
import { Injectable } from '@angular/core';
import { Subscription, Observer, Observable } from 'rxjs';

/* User defined library/components */
import { BootstrapParams } from '../../../../../bootstrap-params';
import { HttpService } from '../../../../core/http.service';
import { ExamFeatureService } from '../../../../shared/services/exam-feature.service';
import { AppointmentService } from '../appointment/appointment.service';
import { MetricApiService } from '../metric-api.service';
import { RegistrationApiService } from '../registration-api.service';
import { SelectedExamDetailService } from '../selected-exam-detail.service';

/* User defined models */
import { Eligibility, EligibilityDetail } from '../../../../shared/models/scheduling/eligibility.model';
import { Exam } from '../../../../shared/models/scheduling/exam.model';
import { AppointmentDetail } from '../../../../shared/models/scheduling/appointmentdetail.model';
import { Payment } from '../../../../shared/models/scheduling/payment.model';
import { AppointmentSlot } from '../../../../shared/models/scheduling/appointmentSlot.model';
import { SiteSlot } from '../../../../shared/models/scheduling/siteSlot.model';
import { Client } from '../../../../shared/models/scheduling/client.model';
import { Program } from '../../../../shared/models/scheduling/program.model';
import { VolumeGovernanceResponse } from '../../../../shared/models/scheduling/volumeGovernanceResponse.model';

/* User defined enums/constants */
import { Fee, LogLevel } from '../../../../shared/enums/scheduling.enum';


@Injectable()
export class EligibilityService {

  public program: Program;
  public client: Client;

  program$: Subscription;
  client$: Subscription;

  constructor(
    private appointmentService: AppointmentService,
    private httpService: HttpService,
    private bootstrapParams: BootstrapParams,
    private registrationApiService: RegistrationApiService,
    private selectedExamDetailService: SelectedExamDetailService,
    private metricApiService: MetricApiService
  ) {
    this.program$ = selectedExamDetailService.program.subscribe(
      program => {
        this.program = program;
      });

    this.client$ = selectedExamDetailService.client.subscribe(
      client => {
        this.client = client;
      });
  }

  public getEligibilityDetails(program: Program, eligibility: Eligibility, confnum?: string) {
    return new Observable((observer: Observer<any>) => {
      this.registrationApiService.getEligibilityDetails(program.code, eligibility.eligibilityDetails.getPostdataUrl(eligibility.eligibilityDetails), confnum).subscribe(
        result => {
          let exams: Exam[] = [];

          this.selectedExamDetailService.setEligibilityDetails(result);

          // get only b2b eligibility exam
          this.selectedExamDetailService.isBackToBackRequired = !!result.filter(eligibility => eligibility.b2b).length;
          this.selectedExamDetailService.scheduleSetAttributeCode = !!result.filter(eligibility => eligibility.scheduleSetAttributeCode).length;
          this.selectedExamDetailService.consecutiveDays = result[0].consecutiveDays;
          this.selectedExamDetailService.maxDays = result[0].maxDays > 0 ? result[0].maxDays : 0;

          if (result[0].maxDays == 0) {
            this.selectedExamDetailService.consecutiveDays = true;
          }

          result.forEach((eligibilityDetail: EligibilityDetail) => {
            exams.push(eligibilityDetail.exam);
          });

          if (exams.length === 1 && this.appointmentService.canScheduleExamInFlow(exams[0])) {
            this.setAppointmentDetails(exams);
          }

          exams.sort(ExamFeatureService.compareExamsForSort);
          observer.next(exams);
          observer.complete();
        });
    });
  }

  public validateLSACSchedulingWindow(program: Program, eligibility: Eligibility): Observable<VolumeGovernanceResponse> {
    const eligibilityCode = eligibility.eligibilityDetails['ELIGIBILITY_NUMBER'];
    const lastName = eligibility.eligibilityDetails['LAST_NAME'];
    return new Observable((observer: Observer<VolumeGovernanceResponse>) => {
      this.httpService.get(this.bootstrapParams.volumeGovernanceAzureFunctionUrl + `?ProgramCode=${program.code}&EligibilityCode=${eligibilityCode}&LastName=${lastName}`, '', null, null, null, null, null, this.bootstrapParams.volumeGovernanceAzureFunctionKey).subscribe({
        next: (result: any) => {
          if (!!result) {
            observer.next(new VolumeGovernanceResponse(result.CanSchedule, result.LastName, result.SchedulingWindowUTC, result.SchedulingWindowUTCText));
          } else {
            this.metricApiService.log(LogLevel.Error, `${this.bootstrapParams.sessionGuid} : Error response from Gee Cache Azure Function: Invalid response`);
            observer.next(new VolumeGovernanceResponse(true, '', '', ''));
          }
          observer.complete();
        },
        error: (err) => {
          if (err.status === 404 && err.error === 'Eligibility record not found.') {
            this.metricApiService.log(LogLevel.Error, `${this.bootstrapParams.sessionGuid} : Not Found response from Gee Cache Azure Function: ${err.status} - ${err.message}`);
          } else if (err.status === 429) {
            this.metricApiService.log(LogLevel.Error, `${this.bootstrapParams.sessionGuid} : Too Many Requests response from Gee Cache Azure Function: ${err.status} - ${err.message}`);
          } else {
            this.metricApiService.log(LogLevel.Error, `${this.bootstrapParams.sessionGuid} : Error response from Gee Cache Azure Function: ${err.status} - ${err.message}`);
          }
          observer.error(err);
          observer.complete();
        }
      });
    });
  }

  setAppointmentDetails(exams: Exam[]) {
    exams.forEach((exam: Exam) => {
      let tempAppointmentDetail: AppointmentDetail = new AppointmentDetail();
      tempAppointmentDetail.exam = exam;
      tempAppointmentDetail.program = this.program;
      tempAppointmentDetail.examName = exam.name;
      tempAppointmentDetail.client = this.client;
      tempAppointmentDetail.payments = [];
      tempAppointmentDetail.appointmentSlot = new AppointmentSlot();
      tempAppointmentDetail.appointmentSlot.site = new SiteSlot(null, null, null, null, null, null);
      let payment: Payment = new Payment(0.0, null, Fee.ExamFee);
      tempAppointmentDetail.payments.push(payment);
      let selectedAppointments = [];
      selectedAppointments.push(tempAppointmentDetail);
      this.selectedExamDetailService.setSelectedAppointmentDetails(selectedAppointments);
      this.selectedExamDetailService.appointmentCountForInfo.next(0);
      this.selectedExamDetailService.setOverlap(selectedAppointments);
      this.selectedExamDetailService.setFormInputData(null);
    });
  }
}
